// src/components/auth/SignUp.tsx
import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { createUserWithEmailAndPassword, sendEmailVerification } from 'firebase/auth';
import { firebaseAuth } from '../../config/firebase'; // Your initialized Firebase Auth instance
import logger from '../../utils/logger';
import './auth.css'; // Import the CSS file

/*
 const handleSignUpWithMock = () => {
    // Replace this with your actual sign-up logic (e.g., GitHub OAuth)
    const mockUserData: AppUser = {
        uid: "123.mock",
        firebaseUser: undefined, // since this is a manually contsructed auth object
        name: "mock user",
        email: "mock@test.com",
        avatarUrl: undefined,
        authProvider: "mock",
        workspaces: [] // None concretely available 
    }        
    signIn(mockUserData); // Call signIn with the user data
    // Redirect to the intended destination or home
    const redirectTo = searchParams.get('redirectTo');
    navigate(redirectTo || '/');
  };
*/

const SignUp: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const getRedirectPath = () => {
    return searchParams.get('redirectTo') || '/';
  };

  const handleSignUp = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      logger.info(`Attempting Firebase user registration for ${email}`);
      const userCredential = await createUserWithEmailAndPassword(firebaseAuth, email, password);
      logger.info(`Firebase user registration successful for ${email}. UID: ${userCredential.user.uid}`);

      // Send email verification
      if (userCredential.user) {
        await sendEmailVerification(userCredential.user);
        logger.info(`Email verification sent to ${email}.`);

        // ToDo: Navigate to a page that says "Please check your email to verify your account"
        // For now, we'll just redirect.
      }

      // onAuthStateChanged in AuthContext will handle setting the user state.
      // Firebase automatically signs the user in upon successful creation.
      navigate(getRedirectPath(), { replace: true });

    } catch (err: any) {
      // Firebase: Error (auth/email-already-in-use)
      let errorMessage = err.message;

      logger.debug( err);
      // Other ERRORS
      // from Firebase production: for exising users: Firebase: Error (auth/invalid-credential).
      // from Firebase production: for non-existing users: 

      if (errorMessage === "Firebase: Error (auth/email-already-in-use).") {
        errorMessage = "Account already exists. Duplicate accounts are NOT permitted.";
      } else {
        logger.error("Firebase user registration error:", err);
      }
      // Handle specific errors if necessary
      setError( errorMessage || 'Failed to sign up. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page-container">
      <div className="auth-form-card">
        <h1 className="auth-title">Create Account</h1>
        <form onSubmit={handleSignUp} className="space-y-4"> {/* Keep space-y-4 for direct children of form */}
          <div>
            <label htmlFor="email-signup" className="auth-label">Email</label>
            <input type="email" id="email-signup" value={email} onChange={(e) => setEmail(e.target.value)} required disabled={loading} className="auth-input" placeholder="you@example.com" />
          </div>
          <div>
            <label htmlFor="password-signup" className="auth-label">Password</label>
            <input type="password" id="password-signup" value={password} onChange={(e) => setPassword(e.target.value)} required disabled={loading} className="auth-input" placeholder="********" />
          </div>
          <div>
            <label htmlFor="confirm-password-signup" className="auth-label">Confirm Password</label>
            <input type="password" id="confirm-password-signup" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required disabled={loading} className="auth-input" placeholder="********" />
          </div>
          <button type="submit" className="deriq-action-button w-full" disabled={loading}>
            {loading ? 'Signing Up...' : 'Sign Up'}
          </button>
        </form>
        {error && <p className="auth-error-message">{error}</p>}
        <p className="auth-link-text">
          Already have an account?{' '}
          <button onClick={() => navigate('/signin')} className="auth-nav-link">
            Sign In
          </button>
        </p>
      </div>
    </div>
  );
};

export default SignUp;
